// composables/useMySuggestions.js
//
// State for «اقتراحاتي» (/suggestions/): the shops she named for products nobody
// could source, and what each one earned her (BE→FE brief
// 2026-10-02-suggestion-reward).
//
// No onMounted here — the page owns invocation and refetches on page-enter. There
// is no polling timer: a reward waits on an admin, so a ticking interval would
// fake liveness the data cannot deliver.
//
// Every amount is printed AS GIVEN. A reward an admin declined sits in no amount
// yet still counts in `helpedMembers`, so a row's two amounts can add up to less
// than `helpedMembers × reward`; nothing here derives one figure from another.
//
// Who was helped is never on the wire and never invented here — counts only
// (owner G-J).
import { computed, ref } from 'vue';
import { mySuggestionsService } from '@/services/mySuggestions.service.js';
import { readSupplierSuggestion } from '@/utils/supplierSuggestion.js';

export const MY_SUGGESTIONS_ERROR = Object.freeze({
  NOT_A_MEMBER: 'not-a-member',
});

const EMPTY_META = Object.freeze({
  total: 0,
  returned: 0,
  hasMore: false,
  earnedCents: 0,
  awaitingCents: 0,
  cappedCents: 0,
  firstDays: null,
  monthlyCapCents: null,
  rewardCents: null,
});

/** A non-negative whole number of piasters, or the fallback. */
function centsOr(value, fallback = 0) {
  return Number.isInteger(value) && value >= 0 ? value : fallback;
}

/** A positive whole number or null — `rewardCents` is ABSENT while the reward is off. */
function positiveOrNull(value) {
  return Number.isInteger(value) && value > 0 ? value : null;
}

/** One row off the wire, defaulted so the page never branches on a missing key. */
export function normalizeSuggestionRow(raw) {
  const product = raw?.product;
  return {
    id: String(raw?.id ?? ''),
    // null only when the product row is gone; `postId` is null when it has no post.
    product:
      product && typeof product === 'object'
        ? {
          id: product.id ?? null,
          name: typeof product.name === 'string' ? product.name.trim() : '',
          postId: product.postId ?? null,
        }
        : null,
    suggestion: readSupplierSuggestion(raw, 'suggestion'),
    helpedMembers: centsOr(raw?.helpedMembers),
    earnedCents: centsOr(raw?.earnedCents),
    awaitingCents: centsOr(raw?.awaitingCents),
  };
}

function normalizeMeta(raw) {
  const meta = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  return {
    total: centsOr(meta.total),
    returned: centsOr(meta.returned),
    hasMore: meta.hasMore === true,
    earnedCents: centsOr(meta.earnedCents),
    awaitingCents: centsOr(meta.awaitingCents),
    cappedCents: centsOr(meta.cappedCents),
    firstDays: positiveOrNull(meta.firstDays),
    monthlyCapCents: positiveOrNull(meta.monthlyCapCents),
    rewardCents: positiveOrNull(meta.rewardCents),
  };
}

/**
 * The refusal's `errors[0].code` (the /suppliers envelope — JSON:API style, the
 * ARRAY form, unlike the referral endpoints' singular `error` object).
 */
function errorCodeOf(err) {
  const code = err?.response?.data?.errors?.[0]?.code;
  return typeof code === 'string' ? code : null;
}

export function useMySuggestions() {
  const rows = ref([]);
  const meta = ref({ ...EMPTY_META });
  const loading = ref(false);
  const error = ref(null);
  const errorCode = ref(null);
  // Has any read settled yet? Keeps the first paint on the skeleton instead of
  // flashing the empty state before the request lands.
  const loaded = ref(false);
  let latestRun = 0;

  const state = computed(() => {
    if (error.value) return 'error';
    if (!loaded.value) return 'loading';
    return rows.value.length === 0 ? 'empty' : 'rows';
  });

  /** Never rejects. */
  async function refresh() {
    const run = ++latestRun;
    loading.value = true;
    try {
      const res = await mySuggestionsService.list();
      // A newer read superseded this one while it was in flight.
      if (run !== latestRun) return;
      const body = res?.data ?? {};
      rows.value = Array.isArray(body.data) ? body.data.map(normalizeSuggestionRow) : [];
      meta.value = normalizeMeta(body.meta);
      error.value = null;
      errorCode.value = null;
    } catch (err) {
      if (run !== latestRun) return;
      // A failed read must not leave the previous rows (or totals) beside an error.
      rows.value = [];
      meta.value = { ...EMPTY_META };
      error.value = err ?? new Error('my-suggestions failed');
      errorCode.value = errorCodeOf(err);
    } finally {
      if (run === latestRun) {
        loading.value = false;
        loaded.value = true;
      }
    }
  }

  return { rows, meta, loading, loaded, error, errorCode, state, refresh };
}
